<template>
  <div class="slide-type-list">
    <div
      v-for="typeObj in allTypeSections"
      :key="typeObj.type"
      class="overflow-hidden"
    >
      <div
        v-if="!isTreatmentSlideTypePreview"
        class="title-table-presentation"
      >
        <span class="text-secondary-100">{{ typeObj.title }} </span>
        <span class="title-table-presentation-tooltip">
          <b-button v-tooltip="typeObj.desc" class="tooltip-btn" />
          <i class="fas fa-question-circle"></i>
        </span>
      </div>
      <div
        v-else
        class="slide-type-section-label"
        :class="{
          'quiz': typeObj.sectionKey === 'quiz',
          'poll': typeObj.sectionKey === 'poll',
          'content': typeObj.sectionKey === 'content',
        }"
      >
        <aha-icon
          :name="typeObj.sectionIcon"
          class="icon mr-2"
          width="21"
          height="21"
          viewBox="0 0 21 21"
        >
        </aha-icon>
        <span class="text-base font-bold">{{ typeObj.sectionLabel }}</span>
      </div>
      <div class="slide-type-section-content">
        <div
          v-for="(item, index) in typeObj.slideTypes"
          v-show="!item.noType"
          :key="item.type + index + item.slideType"
          v-tooltip="item.isLegacy ? boxLegacyTooltip : ''"
          class="box-type"
          :class="{
            beta: item.beta,
            new: item.isNew,
            legacy: item.isLegacy,
            updated: item.isUpdated,
            popular: item.isPopular,
            'coming-soon': item.isComingSoon,
            active: isBoxTypeActive(item),
            'is-eu': item.type === 'openEnded' && isEURegion,
            'hidden': item.hidden
          }"
          @mouseenter="handleHoverOnSlideType(item, 'over', typeObj.sectionLabel)"
          @mouseleave="handleHoverOnSlideType(item, 'out', typeObj.sectionLabel)"
        >
          <div
            class="box-content vertical layout center-justified ml-0.25 relative"
            :class="[item.isLegacy ? 'default': 'pointer']"
            @click="item.isLegacy
              ? false
              : handleSelectSlideType({ item, property: wrapperId })
            "
          >
            <FeatureUpdateTooltipContent
              v-if="isFeatureUpdateTooltipVisible(item)"
              :title="featureTooltipTitle(item.type)"
              :content="featureTooltipContent(item.type)"
            />
            <SlideTypeIcon
              :slide-type="item.type"
              :child-type="item.slideType"
              :left-side="false"
              :active="isBoxTypeActive(item)"
            />
            <div
              v-if="item.type + item.slideType === loadingItem"
              class="loading-show horizontal layout center center-justified"
            >
              <aha-spinner />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import Constant from '@/constant'
import Config from '@/config'
import Util from '@/util'
import eventCategories from '@/constant/eventCategories'

import FeatureUpdateTooltipContent from '@/components-v2/common/tooltip-feature-update/FeatureUpdateTooltipContent.vue'
import SelectSlideTypeMixin from '@/mixins/slide/SelectSlideTypeMixin'

import EventBus from '@/eventBus'

import SlideTypeIcon from '@/components-v2/common/slide-type-icon/SlideTypeIcon.vue'

import imageSubmissionTooltipImage from
  '@/assets/images/feature-update/image-submission-feature-update.svg'
import pollTooltipVideo from '@/assets/images/feature-update/poll.webm'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'

const keyCountOpenEndedUpdateSlide = 'slide-type-open-ended-feature-update'
const keyCountPollUpdate = 'slide-type-poll-update'
const MAX_TIMES_HOVER_BADGE = 10

export default {
  components: {
    SlideTypeIcon,
    FeatureUpdateTooltipContent,
  },
  mixins: [UndoRedoMixin, SelectSlideTypeMixin],
  props: {
    wrapperId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      allTypeSections: [],
      imageSubmissionTooltipImage,
      pollTooltipVideo,
      loadingItem: null,
    }
  },
  computed: {
    ...mapGetters(['isTreatmentSlideTypePreview']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
    }),

    isEURegion() {
      return Config.region === Constant.euRegion
    },
    boxLegacyTooltip() {
      return 'This type is no longer available. Try <strong>‘Content’</strong> instead!'
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.currentUser.id': function () {
      this.handleShowUpdateBadge()
    },
  },

  created() {
    const allTypeSections = [...Constant.allTypeSections]
    this.allTypeSections = this.allTypeSections.concat(allTypeSections).map((item) => {
      item.title = this.$i18nPresenter.t(item.title)
      item.desc = this.$i18nPresenter.t(item.desc)

      return item
    })

    this.preloadImageSubmissionFeatureImage()
    this.handleShowUpdateBadge()
  },

  mounted() {
    this.registerHistory('SideLeft')
  },

  methods: {
    isBoxTypeActive(item) {
      const { type, slideType } = this.slideActive

      return (type === item.type && slideType === item.slideType) ||
        (type === 'multipleChoice' && item.type === 'imageChoice')
    },
    featureTooltipTitle(slideType) {
      if (slideType === 'multipleChoice' || slideType === 'imageChoice') {
        return '<div class="font-bold">👉<span class="pl-1.5">Feature update:</span></div>'
      }
      if (slideType === 'openEnded' && !this.isEURegion) {
        return `
          <div class="font-bold">👉 A new way to answer:</div>
        `
      }

      return ''
    },
    featureTooltipContent(slideType) {
      if ((slideType === 'multipleChoice' || slideType === 'imageChoice')) {
        return `
          <div class="w-40">
            <p class="pb-1">The slide types ‘Multiple Choice’ and ‘Image Choice’ have been merged into <strong>‘Poll’</strong>. Your content is unchanged.</p>
            <video width="174" height="92" id="poll-video" autoplay>
              <source src='${pollTooltipVideo}' type="video/webm">
              Your browser does not support the video tag.
            </video>
          </div>
        `
      }
      if (slideType === 'openEnded' && !this.isEURegion) {
        return `
         <div class="w-40">
            <p class="pb-1">Participants can now respond with photos.</p>
            <img src='${imageSubmissionTooltipImage}' width="174" height="92" />
          </div>
        `
      }

      return ''
    },
    isFeatureUpdateTooltipVisible(slide) {
      if (slide.isUpdated) {
        if (slide.type === 'openEnded' && this.isEURegion) {
          return false
        }

        return true
      }

      return false
    },

    _getKeyUpdateBadge(slideType, isNewKey = false) {
      let key
      const poll = ['multipleChoice', 'imageChoice']

      if ((poll.includes(slideType))) {
        key = keyCountPollUpdate
      }
      if (slideType === 'openEnded' && !this.euRegion) {
        key = keyCountOpenEndedUpdateSlide
      }
      if (isNewKey) {
        key = `${this.currentUser.id}-${key}`
      }

      return key
    },

    _removeAndSetNewKeyUpdateBadge(key) {
      // Old rule: https://ahaslides.atlassian.net/browse/AHA-6520
      // New rule: https://ahaslides.atlassian.net/browse/AHA-7126
      const keyValue = window.localStorage.getItem(key)
      if (keyValue) {
        // Old rule: MAX_TIMES_HOVER_BADGE = 3
        if (keyValue > 2) {
          window.localStorage.setItem(
            `${this.currentUser.id}-${key}`,
            MAX_TIMES_HOVER_BADGE,
          )
        } else {
          window.localStorage.setItem(`${this.currentUser.id}-${key}`, keyValue)
        }
        // If 2 userIds use same browser, 2nd userId won't show badge if (key) => remove old key
        window.localStorage.removeItem(key)
      }
    },

    async handleShowUpdateBadge() {
      try {
        this.allTypeSections.forEach((item) => {
          item.slideTypes.forEach((slide) => {
            if (!slide.isUpdated) return
            const key = this._getKeyUpdateBadge(slide.type)
            this._removeAndSetNewKeyUpdateBadge(key)
            this._setUpdateBadge(
              slide,
              this._getKeyUpdateBadge(slide.type, true),
            )
          })
        })
      } catch {
        // do nothing
      }
    },

    _setUpdateBadge(slide, key) {
      let count
      if (slide.isUpdated) {
        count = window.localStorage.getItem(key) || 0
        if (count == MAX_TIMES_HOVER_BADGE) {
          slide.isUpdated = false

          return
        }
        if (this.currentUser.id) {
          const timeDeployUpdate = 1647837000000 // 21/03/2022 11:30
          const timeUserCreatedAt = new Date(
            this.currentUser.createdAt,
          ).getTime()
          if (timeDeployUpdate < timeUserCreatedAt) {
            slide.isUpdated = false
          }
        }
      }
    },

    preloadImageSubmissionFeatureImage() {
      Util.preloadImage({ url: imageSubmissionTooltipImage })
    },

    handleHoverOnSlideType(item, mode, sectionLabel) {
      if (this.$store.state.isTouchScreen) return

      if (mode === 'over' && this.loadingItem) return

      if (item.isUpdated && (item.type === 'multipleChoice' || item.type === 'imageChoice')) {
        this._setPlayPollVideo(mode)
      }

      if (item.isUpdated && mode === 'over') {
        const key = this._getKeyUpdateBadge(item.type, true)
        this._setCountHoverUpdateBadgeToLocalStorage(key)
      }

      this._trackingHoverSlideType(item, mode, sectionLabel, this.wrapperId)

      EventBus.$emit('handleHoverOnSlideType', item, mode, this.wrapperId)
    },

    _trackingHoverSlideType(item, mode, sectionLabel, touchPoint) {
      if (mode !== 'over') return

      const eventAction = 'Slide type preview >  Hover'
      const customProps = {
        slideType: item.type,
        touchPoint,
        presentationId: this.presentation && this.presentation.id,
        slideId: this.slideActive && this.slideActive.id,
      }

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        eventAction,
        sectionLabel,
        null,
        customProps,
      )
    },

    _setPlayPollVideo(mode) {
      const vid = document.getElementById('poll-video')
      if (!vid) return
      if (mode === 'over') {
        vid.currentTime = 0
        vid.play().catch(() => {})
      } else {
        vid.pause()
      }
    },

    _setCountHoverUpdateBadgeToLocalStorage(key) {
      let count = window.localStorage.getItem(key) || 0
      count++
      if (count <= MAX_TIMES_HOVER_BADGE) {
        window.localStorage.setItem(key, count)
      }
    },

    async handleSelectSlideType({ item, property }) {
      this.loadingItem = item.type + item.slideType
      await this.selectSlideType({ item, property })

      this.$nextTick(() => {
        if (this.wrapperId === 'slide-search-popup') {
          EventBus.$emit('slide-search-popup:close')
        }

        this.loadingItem = null
      })
    },

    restoreSelectSlideType(payload) {
      this.selectSlideType(payload, false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
@import '@/assets/scss-v2/variables.scss';

.fas,
.far,
.fab {
  color: $dark-icon-color;
}

.title-table-presentation {
  @apply text-sm font-bold;

  .title-table-presentation-tooltip {
    font-size: 14px;
    color: #b7b7b7;
  }
}

.box-content {
  @apply ring-1 ring-inset ring-secondary-40;
  display: flex;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  padding: 2px 0 0;
  min-height: 72px;
  transition: 0.5s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
  &:hover {
    @apply shadow-lg;
  }
}

.slide-type-section-label {
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  background-color: rgba(47, 128, 237, 0.15);

  .icon {
    color: #348EF6;
  }
}

.slide-type-section-content {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  padding-top: 12px;
  padding-bottom: 24px;
}

.box-type {
  @apply text-secondary-50;
  text-align: center;
  position: relative;
  cursor: pointer;

  &.is-eu.updated {
    .box-content:after {
      display: none;
    }
  }

  &.beta,
  &.new, &.legacy,
  &.popular,
  &.coming-soon,
  &.updated {
    .box-content {
      &:after {
        display: block;
        background: #FFB12E;
        position: absolute;
        top: 2px;
        padding-left: 20px;
        right: -21px;
        width: 90px;
        height: 16px;
        line-height: 18px;
        text-align: center;
        font-size: 8px;
        font-weight: bold;
        color: #fff;
        -webkit-transform: rotate(45deg);
        transform: rotate(45deg);
        content: 'Beta';
        text-transform: uppercase;
      }
    }
  }

  &.new {
    .box-content {
      &:after {
        @apply bg-primary-90;
        content: 'New';
        font-size: 9px;
        line-height: 16px;
        box-shadow: -1.33274px 2.66548px 5.33096px rgba(52, 142, 246, 0.31);
      }
    }
  }

  &.legacy {
    .box-content {
      &:after {
        @apply bg-secondary-50;
        content: 'Legacy';
        font-size: 9px;
        line-height: 16px;
        box-shadow: -1.33274px 2.66548px 5.33096px rgba(180, 188, 207, 0.24);
      }
    }
    &::v-deep .aha-slide-type-icon {
      opacity: 0.5;
    }
  }

  &.popular {
    .box-content {
      &:after {
        background-color: #ffa826;
        content: 'Popular';
        box-shadow: -1px 2px 4px rgba(255, 177, 46, 0.4);
      }
    }
  }

  &.coming-soon {
    .box-content {
      &:after {
        background-color: #eb68a0;
        content: 'Coming';
        box-shadow: -1px 2px 4px rgba(235, 104, 160, 0.24);
      }
    }
  }

  &.updated {
    .box-content {
      &:after {
        @apply bg-green-50;
        content: 'Updated';
        font-size: 7.5px;
        line-height: 16px;
        box-shadow: -1.33274px 2.66548px 5.33096px rgba(51, 193, 115, 0.15);
      }
    }
  }

  .loading-show {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #f3f3f3ba;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &.active {
    .box-content {
      @apply ring-2 ring-primary-90;
    }
  }
}
</style>
